import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowRight, ArrowLeft, UserPlus, Users, LayoutDashboard } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";

const JoinToday = () => {
  const navigate = useNavigate();

  const steps = [
    {
      icon: UserPlus,
      title: "Create Your Account",
      description: "Sign up at LaunchToRevenue.com/auth to get your credentials.",
      color: "text-blue-600"
    },
    {
      icon: Users,
      title: "Join the Community",
      description: "You'll receive an invite to our Skool hub after account verification.",
      color: "text-purple-600"
    },
    {
      icon: LayoutDashboard,
      title: "Access the Backend Dashboard",
      description: "Log in anytime to use your AI tools, planners, and community hub.",
      color: "text-indigo-600"
    }
  ];

  return (
    <>
      <SEOHead
        title="Join Today | LaunchToRevenue"
        description="Follow these simple steps to access your LaunchToRevenue dashboard and community. Start building, automating, and growing your digital business today."
      />
      
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-indigo-50 dark:from-gray-900 dark:via-purple-900/20 dark:to-blue-900/20">
        {/* Back Arrow */}
        <div className="container mx-auto max-w-6xl px-4 pt-24">
          <Button
            variant="ghost"
            onClick={() => navigate("/")}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </div>
        
        {/* Hero Section */}
        <section className="relative pt-8 pb-20 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center space-y-6 animate-in fade-in duration-1000">
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight">
                Your Path to Launching <br />
                <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-purple-700 bg-clip-text text-transparent">
                  Starts Here 🚀
                </span>
              </h1>
              <p className="text-xl sm:text-2xl text-muted-foreground max-w-3xl mx-auto">
                Follow these simple steps to access your LaunchToRevenue dashboard and community.
              </p>
            </div>
          </div>
        </section>

        {/* Steps Section */}
        <section className="py-12 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="grid md:grid-cols-3 gap-8">
              {steps.map((step, index) => (
                <Card 
                  key={index} 
                  className="relative overflow-hidden border-2 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                >
                  <CardHeader>
                    <div className={`w-16 h-16 rounded-full bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 flex items-center justify-center mb-4`}>
                      <step.icon className={`w-8 h-8 ${step.color}`} />
                    </div>
                    <div className="absolute top-4 right-4 text-6xl font-bold text-gray-100 dark:text-gray-800">
                      {index + 1}
                    </div>
                    <CardTitle className="text-2xl">{step.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base">
                      {step.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4">
          <div className="container mx-auto max-w-4xl">
            <Card className="border-2 bg-gradient-to-br from-primary/5 to-accent/5">
              <CardHeader className="text-center space-y-4 pb-8">
                <CardTitle className="text-3xl sm:text-4xl font-bold">
                  Ready to Transform Your Business?
                </CardTitle>
                <CardDescription className="text-lg">
                  Join thousands of creators who are already building, automating, and scaling their digital businesses.
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg"
                  className="text-base px-8 py-6 h-auto rounded-full shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/40 transition-all"
                  onClick={() => window.open("https://launchtorevenue.com/auth", "_blank")}
                >
                  Sign Up for Access <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="text-base px-8 py-6 h-auto rounded-full"
                  onClick={() => window.open("https://launchtorevenue.com/auth", "_blank")}
                >
                  Already a Member? Log In
                </Button>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Visual Preview Section */}
        <section className="py-12 px-4 pb-20">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold mb-4">Your Complete Business Ecosystem</h2>
              <p className="text-muted-foreground text-lg">
                Access powerful tools, AI assistance, and a thriving community all in one place.
              </p>
            </div>
            <div className="rounded-xl overflow-hidden shadow-2xl border-4 border-primary/20 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 p-8">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="text-center space-y-2">
                  <div className="w-12 h-12 mx-auto rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                    <LayoutDashboard className="w-6 h-6 text-white" />
                  </div>
                  <p className="font-semibold">Dashboard</p>
                </div>
                <div className="text-center space-y-2">
                  <div className="w-12 h-12 mx-auto rounded-full bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <p className="font-semibold">Community</p>
                </div>
                <div className="text-center space-y-2">
                  <div className="w-12 h-12 mx-auto rounded-full bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center">
                    <span className="text-white font-bold">AI</span>
                  </div>
                  <p className="font-semibold">AI Tools</p>
                </div>
                <div className="text-center space-y-2">
                  <div className="w-12 h-12 mx-auto rounded-full bg-gradient-to-br from-pink-500 to-orange-600 flex items-center justify-center">
                    <span className="text-white font-bold">$</span>
                  </div>
                  <p className="font-semibold">Monetization</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default JoinToday;
